import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  Clock,
  HousePlus,
  MapPin,
  Music,
  RefreshCw,
  SquarePen,
} from "lucide-react";
import {
  AvailableSubscriptionSlot,
  SubscriptionRequestDto,
  useAvailableSubscriptionSlotsQuery,
  useCreateSubscriptionMutation,
} from "../../api/subscriptionApi.ts";
import { format } from "date-fns";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getTranslation,
  InstrumentWithFilters,
} from "../../api/instrumentApi.ts";
import {
  TeacherLessonPrice,
  TeacherLocation,
  useTeacherLessonPricesQuery,
  useTeacherLocationsQuery,
  useTeacherQuery,
} from "../../api/teacherApi.ts";
import { ProfilePicture } from "../profile/ProfilePicture.tsx";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { LocationType } from "../../api/locationApi.ts";
import ActionButton from "../ActionButton.tsx";
import { useQueryNav } from "../../hooks/useQueryNav.ts";
import { Weekday, weekdays } from "../Weekdays.ts";

// -------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------

interface SubscriptionProps {
  teacherId: string;
  locationIdQuery: string | null;
  lessonTypeQuery: string | null;
  instrumentQuery: string | null;
}

/**
 * Reference dates for each weekday used to generate locale-aware labels
 * via date-fns format().  2025-01-06 is a confirmed Monday.
 */
const WEEKDAY_REF_DATES: Record<Weekday, Date> = {
  MONDAY: new Date(2025, 0, 6),
  TUESDAY: new Date(2025, 0, 7),
  WEDNESDAY: new Date(2025, 0, 8),
  THURSDAY: new Date(2025, 0, 9),
  FRIDAY: new Date(2025, 0, 10),
  SATURDAY: new Date(2025, 0, 11),
  SUNDAY: new Date(2025, 0, 12),
};

// -------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------

const NewSubscription: React.FC<SubscriptionProps> = ({
  teacherId,
  locationIdQuery,
  lessonTypeQuery,
  instrumentQuery,
}) => {
  const { t } = useLingui();

  // --- selection state ---
  const [selectedTeacherLessonPrice, setSelectedTeacherLessonPrice] =
    useState<TeacherLessonPrice>();
  const [selectedInstrument, setSelectedInstrument] =
    useState<InstrumentWithFilters>();
  const [selectedLocation, setSelectedLocation] = useState<TeacherLocation>();
  const [selectedLessonType, setSelectedLessonType] =
    useState<Exclude<LocationType, "BOTH">>("IN_PERSON");

  const [editingLocation, setEditingLocation] = useState(false);
  const [editingLessonType, setEditingLessonType] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(false);

  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<Weekday>();
  const [selectedTimeSlot, setSelectedTimeSlot] =
    useState<AvailableSubscriptionSlot>();
  const [isDayInitialized, setIsDayInitialized] = useState(false);

  // --- queries / mutations ---
  const { mutate: createSubscription, isPending } =
    useCreateSubscriptionMutation();
  const { data: teacher } = useTeacherQuery(teacherId);
  const { data: locations } = useTeacherLocationsQuery(teacherId);
  const { language, dateFnsLocale } = useLanguageStore();
  const { data: teacherLessonPricesDto } = useTeacherLessonPricesQuery(
    teacherId,
    "SUBSCRIPTION",
  );
  const navigateTo = useQueryNav();

  const locationsWithInstruments = useMemo(
    () =>
      locations
        ? locations.locations.filter((l) => l.instruments.length > 0)
        : [],
    [locations],
  );

  const { data: availableSlotsDto } = useAvailableSubscriptionSlotsQuery(
    teacherId,
    selectedLocation?.id,
    selectedTeacherLessonPrice?.durationInMinutes,
  );

  const availableSlotsByDay = useMemo<
    Partial<Record<Weekday, AvailableSubscriptionSlot[]>>
  >(() => {
    const result: Partial<Record<Weekday, AvailableSubscriptionSlot[]>> = {};
    for (const slot of availableSlotsDto?.slots ?? []) {
      (result[slot.dayOfWeek] ??= []).push(slot);
    }
    return result;
  }, [availableSlotsDto]);

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  // Lesson type from query / location
  useEffect(() => {
    if (!selectedLocation) return;
    const supported =
      lessonTypeQuery &&
      (selectedLocation.locationType === "BOTH" ||
        selectedLocation.locationType.includes(lessonTypeQuery));
    if (supported) {
      setSelectedLessonType(lessonTypeQuery as Exclude<LocationType, "BOTH">);
    } else {
      setSelectedLessonType(
        selectedLocation.locationType === "BOTH"
          ? "IN_PERSON"
          : selectedLocation.locationType,
      );
    }
    setEditingLessonType(false);
  }, [lessonTypeQuery, selectedLocation]);

  // Default lesson price
  useEffect(() => {
    setSelectedTeacherLessonPrice(teacherLessonPricesDto?.prices[0]);
  }, [teacherLessonPricesDto]);

  // Location from query param
  useEffect(() => {
    const locationFromQuery = locations?.locations.find(
      (l) => l.id === locationIdQuery,
    );
    if (locationFromQuery) {
      setSelectedLocation(locationFromQuery);
      setEditingLocation(false);
    } else {
      setEditingLocation(true);
    }
  }, [locationIdQuery, locations]);

  // Instrument from query param
  useEffect(() => {
    const instrument = selectedLocation?.instruments.find(
      (v) => v.name === instrumentQuery,
    );
    if (instrument) {
      setSelectedInstrument(instrument);
      setEditingInstrument(false);
    } else {
      setEditingInstrument(true);
    }
  }, [instrumentQuery, selectedLocation?.instruments]);

  // Reset when location changes
  useEffect(() => {
    setSelectedDayOfWeek(undefined);
    setSelectedTimeSlot(undefined);
    setIsDayInitialized(false);
  }, [selectedLocation]);

  // Reset when duration changes
  useEffect(() => {
    setSelectedDayOfWeek(undefined);
    setSelectedTimeSlot(undefined);
    setIsDayInitialized(false);
  }, [selectedTeacherLessonPrice]);

  // Auto-select first available day when slots load
  useEffect(() => {
    if (isDayInitialized || Object.keys(availableSlotsByDay).length === 0)
      return;
    const firstDay = weekdays.find(
      (w) => (availableSlotsByDay[w.value]?.length ?? 0) > 0,
    );
    if (firstDay) {
      setSelectedDayOfWeek(firstDay.value);
      setIsDayInitialized(true);
    }
  }, [availableSlotsByDay, isDayInitialized]);

  // Auto-select first time slot when day changes
  useEffect(() => {
    if (!selectedDayOfWeek) {
      setSelectedTimeSlot(undefined);
      return;
    }
    const slots = availableSlotsByDay[selectedDayOfWeek];
    setSelectedTimeSlot(slots?.[0]);
  }, [selectedDayOfWeek, availableSlotsByDay]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleSubscription = () => {
    if (
      !selectedTeacherLessonPrice ||
      !selectedInstrument ||
      !selectedLocation ||
      !selectedDayOfWeek ||
      !selectedTimeSlot
    )
      return;

    const subscriptionRequestDto: SubscriptionRequestDto = {
      teacherId,
      locationId: selectedLocation.id,
      instrument: selectedInstrument.name,
      dayOfWeek: selectedDayOfWeek,
      timeZone: selectedTimeSlot.timeZone,
      from: selectedTimeSlot.from.substring(0, 5),
      to: selectedTimeSlot.to.substring(0, 5),
    };

    createSubscription(subscriptionRequestDto, {
      onSuccess: (subscription) =>
        navigateTo(`/subscriptions/${subscription.id}/success`),
      onError: () => console.error("error while creating subscription"),
    });
  };

  // -------------------------------------------------------------------------
  // Derived UI helpers
  // -------------------------------------------------------------------------

  const isSubscriptionValid =
    selectedInstrument &&
    selectedLocation &&
    selectedLessonType &&
    selectedDayOfWeek &&
    selectedTimeSlot &&
    selectedTeacherLessonPrice;

  // Monthly price = weekly lesson price × 4 (approx. 4 lessons per month)
  const monthlyPrice =
    selectedTeacherLessonPrice != null
      ? selectedTeacherLessonPrice.price.toFixed(2)
      : null;

  const getAddress = (location: TeacherLocation) =>
    `${location.countryCode}, ${location.city}`;

  const hasRemote = (location?: TeacherLocation) =>
    !!location && location.locationType !== "IN_PERSON";

  /** Locale-aware abbreviated weekday name, e.g. "Mon" / "Mo" */
  const getDayAbbrev = (day: Weekday) =>
    format(WEEKDAY_REF_DATES[day], "EEE", { locale: dateFnsLocale });

  /** Locale-aware full weekday name, e.g. "Monday" / "Montag" */
  const getDayFullName = (day: Weekday) =>
    format(WEEKDAY_REF_DATES[day], "EEEE", { locale: dateFnsLocale });

  function getLessonTypeButton(
    lessonType: Exclude<LocationType, "BOTH">,
    label: string,
    description: string,
  ) {
    return (
      <button
        onClick={() => {
          setSelectedLessonType(lessonType);
          setEditingLessonType(false);
        }}
        className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
          selectedLessonType === lessonType
            ? "border-soluud-primary bg-soluud-info"
            : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <div className="font-medium">{label}</div>
        <div className="mt-1 text-xs text-gray-500">{description}</div>
      </button>
    );
  }

  function getLessonTypesFromLocation(location: TeacherLocation | undefined) {
    if (!location) {
      return <Trans>Select a location to view possible lesson types.</Trans>;
    }
    const inPerson = getLessonTypeButton(
      "IN_PERSON",
      t`In Person`,
      t`Face-to-face lesson at the location`,
    );
    const remote = getLessonTypeButton(
      "REMOTE",
      t`Remote`,
      t`Online lesson via video call`,
    );
    switch (location.locationType) {
      case "BOTH":
        return (
          <div className="mb-3 flex flex-col gap-3 sm:flex-row">
            {inPerson} {remote}
          </div>
        );
      case "IN_PERSON":
        return <div className="mb-3">{inPerson}</div>;
      case "REMOTE":
        return <div className="mb-3">{remote}</div>;
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="mx-auto max-w-4xl bg-white p-6">
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">
          <Trans>Subscribe to Music Lessons</Trans>
        </h1>
        <p className="text-gray-600">
          {t`Choose a recurring weekly slot that works for you`}
        </p>
      </div>

      {/* Compact teacher info — one line above the heading */}
      {teacher && (
        <div className="mb-5 flex items-center gap-3">
          <div className="ring-soluud-primary/10 h-9 w-9 shrink-0 overflow-hidden rounded-full ring-2">
            <ProfilePicture
              profilePictureUrl={teacher.profilePictureUrl}
              firstName={teacher.firstName}
              lastName={teacher.lastName}
              className="h-full w-full"
              iconSize={16}
            />
          </div>
          <span className="text-sm font-semibold text-gray-900">
            {teacher.firstName} {teacher.lastName}
          </span>
        </div>
      )}

      <div className="space-y-6">
        {/* Location Selection */}
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">
            <MapPin className="mr-2 inline h-4 w-4" />
            <Trans>Location</Trans>
          </label>
          <div className="space-y-2">
            {editingLocation ? (
              locationsWithInstruments?.map((location) => {
                const locationAddress = getAddress(location);
                return (
                  <button
                    key={location.id}
                    onClick={() => {
                      setSelectedLocation(location);
                      setEditingLocation(false);
                    }}
                    className={`w-full rounded-lg border p-4 text-left transition-colors ${
                      selectedLocation?.id === location.id
                        ? "border-soluud-primary bg-soluud-info"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{location.name}</div>
                        {locationAddress && (
                          <div className="mt-1 text-sm text-gray-500">
                            {locationAddress}
                          </div>
                        )}
                      </div>
                      {hasRemote(location) && (
                        <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                          <Trans>Remote Available</Trans>
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="flex items-center font-medium">
                <div className="border-soluud-primary rounded-full border px-4">
                  {selectedLocation?.city}, {selectedLocation?.name}
                </div>
                <SquarePen
                  className="ml-2 inline h-4 w-4 hover:cursor-pointer"
                  onClick={() => setEditingLocation(true)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Instrument Selection */}
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">
            <Music className="mr-2 inline h-4 w-4" />
            <Trans>Select Instrument</Trans>
          </label>
          <div className="space-y-2">
            {editingInstrument ? (
              selectedLocation?.instruments.map((instrument) => (
                <button
                  key={instrument.name}
                  onClick={() => {
                    setSelectedInstrument(instrument);
                    setEditingInstrument(false);
                  }}
                  className={`w-full rounded-lg border p-3 text-left font-medium transition-colors ${
                    selectedInstrument?.name === instrument.name
                      ? "border-soluud-primary bg-soluud-info"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <span>{getTranslation(instrument, language)}</span>
                </button>
              ))
            ) : (
              <div className="flex items-center font-medium">
                <div className="border-soluud-primary rounded-full border px-4">
                  {selectedInstrument ? (
                    getTranslation(selectedInstrument, language)
                  ) : (
                    <Trans>Unknown Instrument</Trans>
                  )}
                </div>
                <SquarePen
                  className="ml-2 inline h-4 w-4 hover:cursor-pointer"
                  onClick={() => setEditingInstrument(true)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Lesson Type Selection */}
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">
            <HousePlus className="mr-2 inline h-4 w-4" />
            <Trans>Lesson Type</Trans>
          </label>
          {editingLessonType ? (
            <div>{getLessonTypesFromLocation(selectedLocation)}</div>
          ) : (
            <div className="flex items-center font-medium">
              <div className="border-soluud-primary rounded-full border px-4">
                {selectedLessonType === "IN_PERSON" ? (
                  <Trans>In Person</Trans>
                ) : (
                  <Trans>Remote</Trans>
                )}
              </div>
              <SquarePen
                className="ml-2 inline h-4 w-4 hover:cursor-pointer"
                onClick={() => setEditingLessonType(true)}
              />
            </div>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Recurring slot selection                                             */}
      {/* ------------------------------------------------------------------ */}
      <div className="mt-12 space-y-6">
        <div className="flex flex-col gap-6 md:flex-row">
          {/* Day + Time Column */}
          <div className="flex-1 space-y-6">
            {/* Day of Week Selector */}
            <div>
              <label className="mb-3 block text-sm font-medium text-gray-700">
                <CalendarDays className="mr-2 inline h-4 w-4" />
                <Trans>Recurring Day</Trans>
              </label>

              <div className="flex flex-wrap gap-2">
                {weekdays.map((wd) => {
                  const slots = availableSlotsByDay[wd.value];
                  const hasSlots = (slots?.length ?? 0) > 0;
                  const isSelected = selectedDayOfWeek === wd.value;

                  return (
                    <button
                      key={wd.value}
                      disabled={!hasSlots}
                      onClick={() => {
                        setSelectedDayOfWeek(wd.value);
                        setIsDayInitialized(true);
                      }}
                      className={`flex flex-col items-center rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                        isSelected
                          ? "border-soluud-primary bg-soluud-info"
                          : hasSlots
                            ? "border-gray-300 hover:border-gray-400"
                            : "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300"
                      }`}
                    >
                      <span>{getDayAbbrev(wd.value)}</span>
                      {hasSlots && (
                        <span
                          className={`mt-0.5 text-xs ${isSelected ? "text-soluud-primary" : "text-gray-400"}`}
                        >
                          {slots!.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {!selectedLocation && (
                <p className="mt-2 text-xs text-gray-400">
                  <Trans>Select a location to see available days.</Trans>
                </p>
              )}
              {selectedLocation && !selectedTeacherLessonPrice && (
                <p className="mt-2 text-xs text-gray-400">
                  <Trans>Select a lesson duration to see available days.</Trans>
                </p>
              )}
              {selectedLocation &&
                selectedTeacherLessonPrice &&
                Object.keys(availableSlotsByDay).length === 0 && (
                  <p className="mt-2 text-xs text-gray-400">
                    <Trans>
                      No recurring slots available for this duration.
                    </Trans>
                  </p>
                )}
            </div>

            {/* Time Slot Selector */}
            <div>
              <label className="mb-3 block text-sm font-medium text-gray-700">
                <Clock className="mr-2 inline h-4 w-4" />
                <Trans>Start Time</Trans>
                {selectedDayOfWeek && (
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    — <Trans>every</Trans> {getDayFullName(selectedDayOfWeek)}
                  </span>
                )}
              </label>

              {selectedDayOfWeek &&
              (availableSlotsByDay[selectedDayOfWeek]?.length ?? 0) > 0 ? (
                <div className="grid grid-cols-3 gap-3">
                  {availableSlotsByDay[selectedDayOfWeek]!.map((slot) => (
                    <button
                      key={slot.from}
                      onClick={() => setSelectedTimeSlot(slot)}
                      className={`rounded-lg border p-3 text-sm font-medium transition-colors ${
                        selectedTimeSlot?.from === slot.from
                          ? "border-soluud-primary bg-soluud-info"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {slot.from.substring(0, 5)}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                  <p className="text-gray-500">
                    {!selectedDayOfWeek
                      ? t`Select a day to see available time slots.`
                      : t`No available time slots for this day.`}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Duration Column */}
          <div className="flex-1">
            <label className="mb-3 block text-sm font-medium text-gray-700">
              <Clock className="mr-2 inline h-4 w-4" />
              <Trans>Lesson Duration</Trans>
            </label>

            {teacherLessonPricesDto?.prices.length ? (
              <div className="flex flex-col gap-3">
                {teacherLessonPricesDto.prices.map((price) => (
                  <button
                    key={price.durationInMinutes}
                    onClick={() => setSelectedTeacherLessonPrice(price)}
                    className={`rounded-lg border p-3 text-sm font-medium transition-colors ${
                      selectedTeacherLessonPrice?.durationInMinutes ===
                      price.durationInMinutes
                        ? "border-soluud-primary bg-soluud-info"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    {price.durationInMinutes} <Trans>minutes</Trans>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                <p className="text-sm text-gray-500">
                  <Trans>
                    This teacher has no subscription prices set up yet.
                  </Trans>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Selected Slot Summary */}
        {selectedDayOfWeek && selectedTimeSlot && (
          <div className="border-soluud-primary bg-soluud-info flex items-center gap-4 rounded-lg border px-4 py-3">
            <RefreshCw className="text-soluud-primary h-5 w-5 shrink-0" />
            <div className="text-sm font-medium">
              <span className="mr-1 text-gray-600">
                <Trans>Recurring every</Trans>
              </span>
              <span className="font-semibold">
                {getDayFullName(selectedDayOfWeek)}
              </span>
              <span className="mx-2 text-gray-400">·</span>
              <span className="font-semibold">
                {selectedTimeSlot.from.substring(0, 5)}
              </span>
              <span className="mx-1 text-gray-400">–</span>
              <span className="font-semibold">
                {selectedTimeSlot.to.substring(0, 5)}
              </span>
            </div>
          </div>
        )}

        {/* Booking conflict hint */}
        {(selectedTimeSlot?.conflictingBookingsCount ?? 0) > 0 && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p>
              <Plural
                value={selectedTimeSlot!.conflictingBookingsCount}
                one="There is one one-off booking scheduled at this time in the future."
                other="There are # one-off bookings scheduled at this time in the future."
              />{" "}
              <Trans>
                You will get a discount on your next billing for each lesson
                that was already booked when your subscription started. Of
                course we will block your slot for new bookings from now on.
              </Trans>
            </p>
          </div>
        )}

        {/* Price Display */}
        <div className="border-soluud-primary rounded-lg border p-4">
          <div className="flex items-center justify-between gap-x-2">
            <div>
              <h3 className="font-semibold">
                <Trans>Monthly Price</Trans>
              </h3>
              <p className="text-sm text-gray-500">
                {selectedTeacherLessonPrice?.durationInMinutes ?? "—"}{" "}
                <Trans>minutes per lesson</Trans>
                {" · "}
                {selectedLessonType === "IN_PERSON" ? (
                  <Trans>in person</Trans>
                ) : (
                  <Trans>remote</Trans>
                )}
                {" · "}
                <Trans>recurring weekly</Trans>
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">€{monthlyPrice ?? "—"}</div>
              <div className="text-xs text-gray-500">
                <Trans>per month</Trans>
              </div>
            </div>
          </div>
        </div>

        {/* Subscribe Button */}
        <div className="flex justify-end">
          <ActionButton
            type="button"
            disabled={!isSubscriptionValid}
            onClick={handleSubscription}
            isLoading={isPending}
          >
            <Trans>Request Subscription</Trans>
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

export default NewSubscription;
